<template>
  <b-card
    class="overflow-hidden"
    no-body
  >
    <b-table
      show-empty
      :busy="loading"
      :items="customers"
      :fields="fields"
      responsive
    >
      <template #table-busy>
        <b-skeleton-table
          :rows="5"
          :columns="fields.length"
          :table-props="{ small: true }"
        />
      </template>

      <template #empty>
        <table-empty-state
          icon="UsersIcon"
          title="Ningún cliente coincide"
          hint="Prueba con otro nombre, correo, empresa o teléfono."
        />
      </template>

      <!-- Nombre -->
      <template #cell(name)="data">
        <router-link
          :to="{ name: 'projects', params: { customerId: data.item.id } }"
          class="text-primary font-weight-bolder"
        >
          {{ data.value }}
        </router-link>
      </template>

      <!-- Email -->
      <template #cell(email)="data">
        <div>
          {{ data.value }}
        </div>
      </template>

      <!-- Fecha de creación -->
      <template #cell(created_at)="data">
        <div>{{ data.value | date }}</div>
      </template>

      <!-- Crédito habilitado (boolean) -->
      <template #cell(credit_enabled)="data">
        <b-badge :variant="data.value ? 'success' : 'danger'">
          {{ data.value ? 'Sí' : 'No' }}
        </b-badge>
      </template>

      <!-- Estatus del crédito (string) -->
      <template #cell(credit_status)="data">
        <b-badge :variant="creditStatusVariant(data.value)">
          {{ data.value || '-' }}
        </b-badge>
      </template>

      <!-- Limite de credito -->
      <template #cell(credit_limit)="data">
        <div class="text-right">
          <span v-if="data.item.credit_enabled && data.value != null">
            $ {{ data.value | money }}
          </span>
          <span
            v-else
            class="text-muted"
          >—</span>
        </div>
      </template>

      <!-- Estado de openQuotation -->
      <template #cell(openQuotation)="data">
        <div class="text-align-center w-100">
          <label class="switch">
            <input
              :checked="data.value"
              type="checkbox"
              @change="(event) => handleSwitchToggle(event, data.item)"
            >
            <span
              id="switchSlider"
              :class="[data.value ? '' : 'closed', 'slider round']"
            />
          </label>
        </div>
      </template>

      <!-- Acciones -->
      <!-- Membresia -->
      <template #cell(membership_plan_name)="data">
        <b-dropdown
          size="sm"
          variant="flat-primary"
          :disabled="membershipUpdatingId === data.item.id"
          :text="membershipUpdatingId === data.item.id
            ? 'Aplicando...'
            : (data.item.membership_plan_name || 'FREE')"
        >
          <b-dropdown-item @click="changeMembership(data.item, 'pro')">
            Activar PRO
          </b-dropdown-item>
          <b-dropdown-item @click="changeMembership(data.item, 'free')">
            Pasar a FREE
          </b-dropdown-item>
        </b-dropdown>
        <small
          v-if="data.item.membership_expires_at"
          class="text-muted d-block"
        >
          Hasta {{ formatMembershipDate(data.item.membership_expires_at) }}
        </small>
      </template>

      <template #cell(tools)="data">
        <b-dropdown
          id="dropdown-1"
          size="sm"
          dropright
          no-caret
          variant="outline-none"
        >
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="18"
              color="#000"
            />
          </template>
          <b-dropdown-item @click="openEditCustomer(data.item)">
            {{ $t('edit') }}
          </b-dropdown-item>
          <!-- <b-dropdown-item @click="deactivateCustomer(data.item.id)">{{
            $t('deactivate')
          }}</b-dropdown-item> -->
          <b-dropdown-item
            v-b-modal.confirmDelete
            variant="danger"
            @click="customerToDeleteId = data.item.id"
          >{{ $t('delete') }}</b-dropdown-item>
        </b-dropdown>
      </template>
    </b-table>

    <!-- Modal de confirmación -->
    <confirmation-modal
      :id="`confirmDelete`"
      :variant="'danger'"
      @confirm="removeCustomer"
    >
      <template v-slot:content>
        {{ $t('deleteCustomerConfirmation') }}
      </template>
    </confirmation-modal>

    <b-modal
      id="edit-customer-credit-modal"
      ref="editCustomerModal"
      :title="`${$t('edit')} ${$t('customer')}`"
      ok-title="Guardar"
      cancel-title="Cancelar"
      @ok="saveCustomerChanges"
      @hidden="resetEditForm"
    >
      <div v-if="editingCustomer">
        <b-form-group :label="$t('name')">
          <b-form-input
            v-model.trim="editForm.name"
            maxlength="120"
          />
        </b-form-group>

        <b-form-group :label="$t('companyName')">
          <b-form-input
            v-model.trim="editForm.company_name"
            maxlength="160"
          />
        </b-form-group>

        <b-form-group :label="$t('creditEnabled')">
          <b-form-checkbox v-model="editForm.credit_enabled">
            {{ editForm.credit_enabled ? 'Sí' : 'No' }}
          </b-form-checkbox>
        </b-form-group>

        <b-form-group :label="$t('creditStatus')">
          <b-form-select
            v-model="editForm.credit_status"
            :options="creditStatusOptions"
          />
        </b-form-group>

        <b-form-group :label="$t('creditLimit')">
          <currency-input
            v-model="editForm.credit_limit"
            :min="0"
            :precision="2"
          />
        </b-form-group>

        <b-form-group
          v-if="editForm.credit_enabled"
          :label="$t('creditType')"
        >
          <b-form-select
            v-model="editForm.credit_type"
            :options="creditTypeOptions"
            @change="handleCreditTypeChange"
          />
        </b-form-group>

        <b-form-group
          v-if="editForm.credit_enabled && editForm.credit_type === 'checkplus'"
          :label="$t('checkplusNumber')"
        >
          <b-form-input
            v-model.trim="editForm.checkplus_number"
            maxlength="80"
          />
        </b-form-group>
      </div>
    </b-modal>

    <!-- Paginación -->
    <b-pagination
      class="mt-1 ml-1"
      :value="pagination.page"
      :total-rows="pagination.total_objects"
      :per-page="pagination.per_page"
      @change="handlePageChange"
    />
  </b-card>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import { BSkeletonTable } from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ConfirmationModal from '@/components/ConfirmationModal.vue'
import CurrencyInput from '@/components/CurrencyInput.vue'
import TableEmptyState from '@/components/TableEmptyState.vue'

export default {
  components: {
    ConfirmationModal,
    CurrencyInput,
    BSkeletonTable,
    TableEmptyState,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    customers: {
      type: Array,
      default() {
        return []
      },
    },
    pagination: {
      type: Object,
      default: () => [],
    },
  },
  data() {
    return {
      membershipUpdatingId: null,
      // Los anchos se reparten por lo que mide cada dato: el nombre se partia en tres
      // renglones mientras el correo y el telefono sobraban de espacio.
      fields: [
        {
          key: 'name', label: this.$t('name'), thStyle: { minWidth: '13rem' }, class: 'text-left',
        },
        {
          key: 'company_name', label: this.$t('companyName'), thStyle: { minWidth: '11rem' }, class: 'text-left',
        },
        {
          key: 'email', label: this.$t('email'), thStyle: { minWidth: '13rem' }, class: 'text-left',
        },
        {
          key: 'phone', label: this.$t('phone'), thStyle: { minWidth: '8rem' }, class: 'text-center',
        },
        {
          key: 'created_at', label: this.$t('createdAt'), thStyle: { minWidth: '7rem' }, class: 'text-center',
        },
        { key: 'credit_enabled', label: this.$t('creditEnabled'), class: 'text-center' },
        { key: 'credit_status', label: this.$t('creditStatus'), class: 'text-center' },
        { key: 'credit_limit', label: this.$t('creditLimit'), class: 'text-right' },
        { key: 'openQuotation', label: this.$t('openQuotation'), class: 'text-center' },
        { key: 'membership_plan_name', label: this.$t('membership'), class: 'text-center' },
        { key: 'tools', label: this.$t('actions') },
      ],
      customerToDeleteId: null,
      editingCustomer: null,
      editForm: {
        id: null,
        name: '',
        company_name: '',
        credit_enabled: false,
        credit_status: 'inactive',
        credit_limit: 0,
        credit_type: 'yoc',
        checkplus_number: '',
      },
      creditStatusOptions: [
        { value: 'active', text: 'Activo' },
        { value: 'inactive', text: 'Inactivo' },
        { value: 'suspended', text: 'Suspendido' },
        { value: 'delinquent', text: 'Mora' },
      ],
    }
  },
  computed: {
    creditTypeOptions() {
      return [
        { value: 'yoc', text: this.$t('creditTypeYoc') },
        { value: 'checkplus', text: this.$t('creditTypeCheckplus') },
      ]
    },
  },
  methods: {
    ...mapMutations('customers', {
      changePage: 'CHANGE_PAGE',
    }),
    ...mapActions('customers', ['deleteCustomer', 'updateOpenQuotation', 'updateCustomer']),
    ...mapActions('memberships', ['updateCustomerMembership']),
    formatMembershipDate(value) {
      if (!value) return ''
      return new Date(value).toLocaleDateString('es-MX')
    },
    // Mover el plan es cobrable, asi que se confirma antes y se avisa despues: el admin no
    // deberia tener que adivinar si su clic surtio efecto.
    async changeMembership(customer, planKey) {
      const isPro = planKey === 'pro'
      const question = isPro
        ? `Activar el plan PRO a ${customer.name} por 1 mes?`
        : `Pasar a ${customer.name} al plan FREE? Perdera el acceso de paga de inmediato.`

      const confirmed = await this.$bvModal.msgBoxConfirm(question, {
        title: 'Cambiar membresia',
        okTitle: isPro ? 'Activar PRO' : 'Pasar a FREE',
        cancelTitle: 'Cancelar',
        okVariant: isPro ? 'primary' : 'outline-danger',
        centered: true,
      })
      if (!confirmed) return

      try {
        this.membershipUpdatingId = customer.id
        await this.updateCustomerMembership({
          customerId: customer.id,
          planKey,
          months: isPro ? 1 : null,
        })
        this.$emit('refresh', this.pagination.page)
        this.$toast.success(isPro
          ? `${customer.name} quedo en plan PRO por 1 mes.`
          : `${customer.name} regreso al plan FREE.`)
      } catch (error) {
        const messages = error?.data?.messages || ['No se pudo cambiar la membresia.']
        this.$toast.error(messages[0])
      } finally {
        this.membershipUpdatingId = null
      }
    },
    async removeCustomer() {
      try {
        const response = await this.deleteCustomer(this.customerToDeleteId)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: response?.result === 'archived' ? 'Cliente archivado' : 'Cliente eliminado',
            text: response?.messages?.[0],
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
        this.$emit('refresh', this.pagination.page)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'No se pudo eliminar el cliente',
            text: error?.data?.messages?.[0] || 'Inténtalo de nuevo.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
    openEditCustomer(customer) {
      this.editingCustomer = customer
      this.editForm = {
        id: customer.id,
        name: customer.name || '',
        company_name: customer.company_name || '',
        credit_enabled: Boolean(customer.credit_enabled),
        credit_status: customer.credit_status || 'inactive',
        credit_limit: Number(customer.credit_limit || 0),
        credit_type: customer.credit_type || 'yoc',
        checkplus_number: customer.checkplus_number || '',
      }
      this.$refs.editCustomerModal.show()
    },
    resetEditForm() {
      this.editingCustomer = null
      this.editForm = {
        id: null,
        name: '',
        company_name: '',
        credit_enabled: false,
        credit_status: 'inactive',
        credit_limit: 0,
        credit_type: 'yoc',
        checkplus_number: '',
      }
    },
    handleCreditTypeChange(value) {
      if (value !== 'checkplus') {
        this.editForm.checkplus_number = ''
      }
    },
    async saveCustomerChanges(bvModalEvt) {
      if (bvModalEvt && bvModalEvt.preventDefault) bvModalEvt.preventDefault()

      if (this.editForm.credit_enabled && this.editForm.credit_type === 'checkplus' && !String(this.editForm.checkplus_number || '').trim()) {
        this.$toast.error('Debes capturar el numero de Checkplus.')
        return
      }

      try {
        await this.updateCustomer({
          id: this.editForm.id,
          name: this.editForm.name,
          company_name: this.editForm.company_name,
          credit_enabled: this.editForm.credit_enabled,
          credit_status: this.editForm.credit_status,
          credit_limit: Number(this.editForm.credit_limit || 0),
          credit_type: this.editForm.credit_enabled ? this.editForm.credit_type : 'yoc',
          checkplus_number: this.editForm.credit_enabled && this.editForm.credit_type === 'checkplus'
            ? String(this.editForm.checkplus_number || '').trim()
            : null,
        })

        this.$emit('refresh', this.pagination.page)
        this.$refs.editCustomerModal.hide()
        this.$toast.success('Cliente actualizado correctamente.')
      } catch (error) {
        const messages = error?.data?.messages || ['No se pudo actualizar el cliente.']
        this.$toast.error(messages[0])
      }
    },
    creditStatusVariant(status) {
    // normaliza por si viene en mayúsculas/minúsculas
      const s = (status || '').toString().toLowerCase()
      if (s === 'active' || s === 'activo') return 'success'
      if (s === 'inactive' || s === 'inactivo') return 'secondary'
      if (s === 'suspended' || s === 'suspendido') return 'warning'
      if (s === 'delinquent' || s === 'mora') return 'danger'
      return 'light' // fallback
    },
    handlePageChange(page) {
      this.changePage(page)
      this.$emit('refresh', page)
    },
    async handleSwitchToggle(event, customer) {
      const newStatus = event.target.checked
      try {
        // Llamar a la acción de Vuex para actualizar el estado
        await this.$store.dispatch('customers/updateOpenQuotation', {
          customerId: customer.id,
          openQuotation: newStatus,
        })

        // Mostrar notificación de éxito
        this.$toast.success(
          `Se actualizo las cotizaciones a ${
            newStatus ? '"ABIERTA"' : '"CERRADA"'
          }.`,
        )

        // Actualizar el estado localmente
        customer.openQuotation = newStatus
      } catch (error) {
        console.error('Error al actualizar el estado:', error)
        this.$toast.error('Hubo un problema al actualizar el estado.')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.switch {
  margin-right: -18px;
  position: relative;
  display: inline-block;
  width: 28px;
  height: 18px;
}

/* Ocultar el checkbox predeterminado */
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* El slider */
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #04ff1d;
  transition: 0.4s;
}

.slider:before {
  position: absolute;
  content: '';
  height: 10px;
  width: 10px;
  left: 4px;
  bottom: 3px;
  background-color: #ffffff;
  transition: 0.4s;
  border: 1px solid #ffffff;
}

.slider.closed:before {
  border: 1px solid #04ff1d;
}

input:checked + .slider {
  background-color: #04ff1d;
}

input:focus + .slider {
  background-color: #04ff1d;
}

input:checked + .slider:before {
  transform: translateX(9px);
}

/* Redondear el slider */
.slider.round {
  border-radius: 34px;
  border: 1px solid #04ff1d;
}

.slider.round.closed {
  border: 1px solid #04ff1d;
}

.slider.round:before {
  border-radius: 50%;
}

.text-align-center {
  text-align: center;
}
</style>
